import axios from 'axios';

import { useChannelPrefs } from 'src/api/comms/hooks';

/**
 * Whether the current principal has a personal account behind it.
 *
 * - `linked` — there is a Contact: the account surfaces have real data.
 * - `unlinked` — there is none: every account surface degrades to the
 *   "Sign in to manage your preferences" empty state.
 * - `unknown` — the probe is still in flight.
 */
export type LinkedPrincipalState = 'linked' | 'unlinked' | 'unknown';

/**
 * Two kinds of principal reach the portal without a personal account:
 *
 * 1. the anonymous address-search login (`/login` answers `anonymous: true`);
 * 2. the machine PropertyUser the admin platform's "Open portal" link mints
 *    (`anonymous: false`, `created_by_system: true`, no ssn / tax_id / email /
 *    name) — so the `anonymous` flag alone does NOT catch it.
 *
 * There is no principal-level field on the `/login` + `property_user/`
 * responses that separates both of those from a linked citizen, so the single
 * rule is the question `CommunicationPreferences` already asks:
 * `GET comms/channels` answers 404 when the session resolves to no Contact.
 * The result is one shared, cached react-query entry (same key), so the nav
 * entry and the route guard always agree and the page pays for the request
 * only once.
 *
 * Anything that is not a definite "no Contact" fails OPEN — a 5xx, a timeout,
 * a network/CORS failure or a query react-query paused while the browser is
 * offline must not hide a real citizen's account page.
 */
export const useLinkedPrincipal = (): LinkedPrincipalState => {
  const { isSuccess, isLoading, error } = useChannelPrefs();

  if (isSuccess) return 'linked';
  if (axios.isAxiosError(error) && error.response?.status === 404) {
    return 'unlinked';
  }
  if (isLoading) return 'unknown';
  return 'linked';
};
